import React, { useState, useEffect, useRef, useCallback } from "react";

import { useLocation, useNavigate, useParams } from "react-router";
import Canvas from "../Canvas/Canvas";
import { CanvasContext } from "../Canvas/WorkflowCanvas";
import useProgressViewStore from "../../ProjectCanvas/useProgressViewStore";

import { send_request } from "../../../utils/Request";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  loadPublishedProject,
  loadProjectByVersion,
} from "../../../utils/canvasApi";
import {
  extractNodes,
  extractEdges,
  isEdgeType,
} from "../../../utils/CanvasUtil";

// Custom Components
import TopGrid from "./TopGrid";
import ProgressViewPageHeader from "./ProgressViewPageHeader";
import ManagedRunOperatorPanel from "./ManagedRunOperatorPanel";
import useProjectStore from "../../ProjectCanvas/useProjectStore";
import { hasManagedRunsAccessForClient } from "../../../constants/featureFlags";
import { calculateTimeElapsedForLink, getColourFromStatus } from "./Util";
import SessionDocumentPanel, { buildSectionKey } from "./SessionDocumentPanel";
import SessionPolling from "./SessionPolling";

// MUI
import { styled } from "@mui/styles";
import {
  collectThreadMappingsWithStatus,
  drawPaths,
  invertThreadPath,
  removeOldThreadPaths,
  replaceOldThreadWithNew,
} from "./LoopSelectionAlgorithm";
import { StyledSkeleton } from "../HomePageComponents/CommonComponents";

const DEFAULT_NODE_COLOR = "var(--color-text-disabled)";
const DEFAULT_LINK_COLOR = "var(--color-text-secondary)";

const RootWrapper = styled("div")(({ isSplitView }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: isSplitView ? "100%" : "100vh",
  minHeight: 0,
  overflow: "hidden",
}));

const CanvasDiv = styled("div")({
  position: "relative",
  width: "100%",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

function WorkflowProgressView(props) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const clientId = useProjectStore((s) => s.clientId);
  const hasManagedRunsAccess = hasManagedRunsAccessForClient(clientId);

  // true/false whether it's a test session or not
  const isTestSession = Boolean(
    (search && search === "?test=true") || props.isTest
  );

  const [loading, setLoading] = useState(false);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Define nodes & edges
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  const [workflow, setWorkflow] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isPolling, setIsPolling] = useState(true);

  const [selectedSectionKey, setSelectedSectionKey] = useState(null);
  const [selectionNonce, setSelectionNonce] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [orderedSessionComponents, setOrderedSessionComponents] = useState([]);
  // Gate the entire interactive UI behind a loading overlay until the
  // SessionDocumentPanel has mounted all its Ace editors. The panel renders
  // hidden in the background during this window so click-to-open is instant.
  const [isReady, setIsReady] = useState(false);

  const [showTerminateButton, setShowTerminateButton] = useState(false);

  const reactFlowWrapper = useRef(null);

  const [threadMapping, setThreadMapping] = useState({});
  const [loopPath, setLoopPath] = useState({});
  const [isMainFlowSelected, setIsMainFlowSelected] = useState(false);

  const [reRuns, setReRuns] = useState(null);
  const [isLatestRun, setIsLatestRun] = useState(true);
  const [loadReRun, setLoadReRun] = useState(false);

  const [draftVersion, setDraftVersion] = useState([]);
  const [productionVersion, setProductionVersion] = useState([]);

  const selectedComponentId = selectedSectionKey
    ? selectedSectionKey.split("::")[0]
    : null;

  const externalSearchQuery = props.externalSearchQuery || "";

  const params = useParams();
  const id = props.id ? props.id : params.id;
  const project_session_id = props.project_session_id
    ? props.project_session_id
    : params.project_session_id;

  let threads = {};

  useEffect(() => {
    const fetchData = async () => {
      resetStateForNewSessionSelected(); // Reset state here
      getWorkflowProgressData();
      getLatestDraftVersion(id);
      getLatestProductionVersion(id);
    };
    fetchData();
  }, [id, project_session_id]);

  useEffect(() => {
    if (externalSearchQuery && externalSearchQuery.trim().length > 0) {
      setIsPanelOpen(true);
    }
    // Re-fire when the session changes so a card click with the same active
    // search still pops the panel for the newly-selected session.
  }, [externalSearchQuery, id, project_session_id]);

  // Once data is loaded and the panel has had a chance to mount its Ace
  // editors in the background, drop the loading overlay. Two RAFs + a
  // 50ms cushion is generally enough for the heavy first paint to commit;
  // larger sessions trade longer wait for instant click-to-open.
  useEffect(() => {
    if (isReady) return undefined;
    if (canvasLoading || !workflow || !sessionData || !reRuns) return undefined;
    let raf2;
    let timer;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        timer = window.setTimeout(() => setIsReady(true), 50);
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2 !== undefined) window.cancelAnimationFrame(raf2);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [isReady, canvasLoading, workflow, sessionData, reRuns]);

  // Function for resetting some of the state when a new progress view session
  // has been selected
  const resetStateForNewSessionSelected = () => {
    setSelectedSectionKey(null);
    setIsPanelOpen(false);
    setIsPanelExpanded(false);
    setOrderedSessionComponents([]);
    setIsReady(false);
    setThreadMapping({});
    setLoopPath({});
    setIsPolling(true);
    setCanvasLoading(true);
  };

  const getReruns = async () => {
    const url = `project-service/project/get-rerun-headers/${id}/${project_session_id}`;

    return await send_request(url, null, null, "GET")
      .then((res) => {
        setReRuns(res.data);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const findComponentSession = (currComp, sessions, type, path) => {
    if (!sessions["components"]) return;

    let sessionComps = sessions["components"];
    let relevantComps = sessionComps.filter(
      (sC) => sC.componentId === currComp.id
    ); // Grab only by component id

    // If it's not a loop or assign task
    if (
      currComp.data.type != "loop_through_list" &&
      currComp.data.type != "sequential_form"
    ) {
      let session = findNonLoopOrAssignTaskSession(relevantComps, path);
      if (session) return session;
    } else if (currComp.data.type === "loop_through_list") {
      // Find Success End and store its thread info
      let firstSuccess = null;
      for (let i = 0; i < relevantComps.length; i++) {
        if (relevantComps[i].status != "SUCCESS") continue;
        let currSuccess = relevantComps[i];
        if (!firstSuccess) firstSuccess = currSuccess; // Keep track of first success for fallback
        if (currSuccess.thread.threadId in path) return currSuccess;
      }
      // If no thread matched in path, return first success so thread selector still renders
      // This handles the "Main Flow" case where path is {'null': []}
      if (firstSuccess) return firstSuccess;
    } else if (currComp.data.type === "sequential_form") {
      // Go through the task sessions
      for (let i = 0; i < relevantComps.length; i++) {
        let currC = relevantComps[i];

        // If a thread doesn't exist and there are no current threads in the pool
        // Return it
        if (!currC.thread && Object.keys(threads).length === 0) return currC;

        // If the status is waiting or terminated, we check if both
        // the threadId and parentThreadID exist in the pool
        // If so, we add the mapping and return the component
        if (currC.status === "WAITING" || currC.status === "TERMINATED") {
          if (
            currC.thread.threadId in threads &&
            (currC.thread.parentThreadId in threads ||
              currC.thread.parentThreadId === "null")
          ) {
            threads[currC.thread.threadId] = currC.thread.parentThreadId;
            return currC;
          }
        } else {
          // Else, we know it must be success
          // So we check if the waiting state thread ids are in the pool
          if (
            (currC.data.waitingStateThreadId in threads &&
              (currC.data.waitingStateParentThreadId in threads ||
                currC.data.waitingStateParentThreadId === "null")) ||
            (currC.data.waitingStateThreadId === "null" &&
              currC.data.waitingStateParentThreadId === "null")
          ) {
            threads[currC.thread.threadId] = currC.thread.parentThreadId;
            return currC;
          }
        }
      }

      // If we haven't found anything, we try to look in the normal case
      let session = findNonLoopOrAssignTaskSession(relevantComps, path);
      if (session) return session;
    }
  };

  // Handles altering a thread path from oldThread => newThread
  const handleAlterThreadPath = (oldThread, newThread) => {
    // Special case: "Main Flow" selected - show parent thread components only
    if (newThread === "MAIN_FLOW") {
      // Create a minimal path that only includes the root parent thread
      // This will cause findNonLoopOrAssignTaskSession to fall through to parentThreadId check
      const mainFlowPath = { null: [] };
      setIsMainFlowSelected(true);
      setLoopPath(mainFlowPath);
      handleLogic(sessionData, invertThreadPath(mainFlowPath), isLatestRun);
      return;
    }

    // Any other thread selection means Main Flow is not selected
    const wasMainFlowSelected = isMainFlowSelected;
    setIsMainFlowSelected(false);

    // When coming from Main Flow, just draw fresh paths from the new thread
    // (the Main Flow loopPath is { 'null': [] } so there's nothing to modify)
    if (wasMainFlowSelected) {
      let newSelectedPath = drawPaths(threadMapping, newThread);
      setLoopPath(newSelectedPath);
      handleLogic(sessionData, invertThreadPath(newSelectedPath), isLatestRun);
      return;
    }

    let currPath = { ...loopPath };

    // #1 - Remove any oldThread mapping
    let newPath = removeOldThreadPaths(oldThread, currPath);

    // #2 - Replace oldThread with newThread in threadIds
    let threadRemovedPath = replaceOldThreadWithNew(
      oldThread,
      newThread,
      newPath,
      "null"
    );

    // #3 - Redraw path from newThread
    let newSelectedPath = drawPaths(threadMapping, newThread);
    const finalPath = { ...threadRemovedPath, ...newSelectedPath }; // Add new paths to old removed path

    setLoopPath(finalPath);
    handleLogic(sessionData, invertThreadPath(finalPath), isLatestRun); // Invert and set in data
  };

  const findNonLoopOrAssignTaskSession = (relevantComps, path) => {
    for (let i = 0; i < relevantComps.length; i++) {
      let currRelevantComp = relevantComps[i];
      if (!currRelevantComp.thread) return currRelevantComp;

      if (currRelevantComp.thread.threadId in path) {
        if (
          path[currRelevantComp.thread.threadId] ===
          currRelevantComp.thread.parentThreadId
        )
          return currRelevantComp;
      }

      // Also match components running on a parent thread (parentThreadId is null or 'null')
      // This handles post-converge_threads components that run on the original parent thread
      const parentId = currRelevantComp.thread.parentThreadId;
      if (parentId === null || parentId === "null") {
        return currRelevantComp;
      }
    }
  };

  // returns true if we should display the terminate button
  const shouldDisplayTerminateButton = (comp) => {
    if (!comp || !comp.data || !comp.data.type) return;
    let type = comp.data.type;
    if (type === "sequential_form" || type === "form_section")
      setShowTerminateButton(true);
  };

  /**
   * Gets the general correct order of the components
   * We do this by looping through the sessions, finding the relevant component and
   * adding it to the reOrdered component list
   * @param {*} comps
   * @param {*} sessions
   * @returns
   */
  const getCorrectOrderOfComponents = (comps, sessions) => {
    let sessionComp = [...sessions.components];

    let links = [];
    let dbCompAndLinks = [];
    let visitedC = [];
    let reorderedComponents = [];

    for (let i = 0; i < sessionComp.length; i++) {
      let foundComp = null;

      for (let j = 0; j < comps.length; j++) {
        if (comps[j].id === sessionComp[i].componentId) {
          foundComp = comps[j];
          break;
        }
      }

      if (!foundComp || visitedC.includes(foundComp.id)) continue;
      reorderedComponents.push(foundComp);
      visitedC.push(foundComp.id);
    }

    // Loop over comps
    for (let i = 0; i < comps.length; i++) {
      if (comps[i].type === "dbNode") {
        dbCompAndLinks.push(comps[i]);
        continue;
      }
      if (isEdgeType(comps[i].type)) {
        if (comps[i].data && comps[i].data.hasDb) dbCompAndLinks.push(comps[i]);
        else links.push(comps[i]);
        continue;
      }
      if (visitedC.includes(comps[i].id)) continue;

      reorderedComponents.push(comps[i]);
      visitedC.push(comps[i].id);
    }

    return {
      comps: reorderedComponents,
      links: links,
      dbCompAndLinks: dbCompAndLinks,
    };
  };
  /**
   * Adds colour to the components (success, waiting, terminated, etc)
   * @param {*} comps
   * @param {*} sessions
   * @returns
   */
  const addColourToComponents = (comps, sessions, path, isLatestReRun) => {
    if (!comps || !sessions) return;
    let correctOrder = getCorrectOrderOfComponents(comps, sessions);

    let sessionComponents = correctOrder.comps;
    const orderedSessions = [];

    // Handle Components
    for (let i = 0; i < sessionComponents.length; i++) {
      let currComp = sessionComponents[i];

      if (sessions.hasWaitingStatus) {
        shouldDisplayTerminateButton(currComp);
      }

      // Try to find the component session
      let currCompSession = findComponentSession(
        currComp,
        sessions,
        "component",
        path
      );

      // Set the type of the node and the initial grey colour
      sessionComponents[i].type = "sessionComponent";
      sessionComponents[i].data.colour = DEFAULT_NODE_COLOR;
      sessionComponents[i].data.baseColor = DEFAULT_NODE_COLOR;

      const isSelectedComp =
        selectedComponentId && selectedComponentId === sessionComponents[i].id;

      // If the session exists, it means it has been run
      if (currCompSession) {
        sessionComponents[i].data.colour = getColourFromStatus(
          currCompSession.status
        );
        sessionComponents[i].data.baseColor = getColourFromStatus(
          currCompSession.status
        );
        sessionComponents[i].data.sessionData = currCompSession;
        sessionComponents[i].data.allSessionData = sessions;
        sessionComponents[i].data.modalData = isSelectedComp
          ? { componentId: sessionComponents[i].id, hasRun: true }
          : { hasRun: true };
        orderedSessions.push({
          ...currCompSession,
          _nodeLabel: sessionComponents[i].data?.label || null,
        });
      } else {
        sessionComponents[i].data.sessionData = null;
        sessionComponents[i].data.modalData = isSelectedComp
          ? { componentId: sessionComponents[i].id }
          : {};
      }

      const nodeComponentId = sessionComponents[i].id;
      sessionComponents[i].data.setModalData = (value) => {
        // Not-yet-run nodes: ProgressCanvasComponent sends
        // { componentId: '', hasRun: false } — fall back to the clicked
        // node's id so the canvas selection highlight persists. No section
        // exists yet, so scroll is a no-op; the document opens at top.
        if (!value || !value.componentId) {
          if (nodeComponentId) {
            setSelectedSectionKey(`${nodeComponentId}::null::latest`);
          } else {
            setSelectedSectionKey(null);
          }
          setIsPanelOpen(true);
          setSelectionNonce((n) => n + 1);
          return;
        }
        setSelectedSectionKey(buildSectionKey(value, sessions));
        setIsPanelOpen(true);
        setSelectionNonce((n) => n + 1);
      };
    }

    setOrderedSessionComponents(orderedSessions);

    // Get the coloured links (pass loopIterations for loop edge counters)
    let compLinks = getColouredComponentLinks(
      correctOrder.links,
      sessionComponents,
      sessions.loopIterations
    );

    // Add the links to the components and return them
    sessionComponents = [
      ...sessionComponents,
      ...compLinks,
      ...correctOrder.dbCompAndLinks,
    ];

    return sessionComponents;
  };

  /**
   * Get the coloured components from the sessions for the links between components
   * We find the source component of the link and set that colour from the source component
   * @param {*} links
   * @param {*} sessions
   * @param {*} loopIterations - Map of loop edge keys to current iteration count
   * @returns
   */
  const getColouredComponentLinks = (links, sessions, loopIterations) => {
    let compLinks = [];

    // Handle links
    for (let i = 0; i < links.length; i++) {
      let newLink = { ...links[i] };

      // We did this because "stroke" is read-only
      let sty = { ...newLink["style"] };
      newLink["style"] = sty;

      // Set to default colour
      newLink.style.stroke = DEFAULT_LINK_COLOR;
      newLink.animated = true;

      let foundSession = null;
      for (let j = 0; j < sessions.length; j++) {
        if (newLink.source === sessions[j].id) {
          foundSession = sessions[j];
          break;
        }
      }

      if (foundSession) {
        newLink.style.stroke = foundSession.data.colour;
        if (foundSession.data.colour != DEFAULT_NODE_COLOR)
          newLink.animated = false;
        newLink["label"] = calculateTimeElapsedForLink(newLink, sessions); // Add time label to link if able
      }

      // For loop links, add the current iteration from loopIterations
      if (newLink.data?.isLoop && loopIterations) {
        const loopKey = `${newLink.source}->${newLink.target}`;
        const currentIteration = loopIterations[loopKey] || 0;
        newLink.data = {
          ...newLink.data,
          currentIteration: currentIteration,
        };
      }

      compLinks.push(newLink);
    }

    return compLinks;
  };

  const getWorkflowData = async (data) => {
    return await loadProjectByVersion(id, data.projectVersion)
      .then((res) => {
        useProgressViewStore.getState().setWorkflow(res.data); //Load diagram into the store
        setWorkflow(res.data); //set diagram into the state
        return res.data;
      })
      .then((projectData) => {
        drawPathAndHandleLogic(data, projectData.components);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const drawPathAndHandleLogic = (sessionData, projectComps) => {
    let mapping = collectThreadMappingsWithStatus(
      sessionData.components,
      projectComps
    );
    let randomPath = drawPaths(mapping, "null");

    setThreadMapping(mapping);
    setLoopPath(randomPath);

    let path = invertThreadPath(randomPath);
    handleLogic(sessionData, path, true);
  };

  const handleLogic = (data, path, isLatestReRun) => {
    let convertedTypes = addColourToComponents(
      useProgressViewStore.getState().canvas,
      data,
      path,
      isLatestReRun
    );
    if (convertedTypes) {
      setNodes(extractNodes(convertedTypes));
      setEdges(extractEdges(convertedTypes));
    }
    threads = {};
  };

  /**
   * Called by SessionPolling when SSE/polling detects a status change.
   * Fetches the full session log and runs the full drawPathAndHandleLogic
   * pipeline — same as the old polling approach, but triggered by SSE
   * change detection instead of blind 1s polling.
   */
  const fetchingRef = useRef(false);
  const onStatusChanged = useCallback(async () => {
    // Debounce: skip if a fetch is already in flight
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    let url = `project-service/project/get_log/${id}/${project_session_id}`;
    if (isTestSession) {
      url = `project-service/test/get_test_log/${id}/${project_session_id}`;
    }

    try {
      const res = await send_request(url, "", {}, "");
      if (res?.data) {
        setSessionData(res.data);
        if (workflow?.components) {
          drawPathAndHandleLogic(res.data, workflow.components);
        }
      }
    } catch (e) {
      // Silently handle fetch errors during live updates
    } finally {
      fetchingRef.current = false;
    }
  }, [id, project_session_id, isTestSession, workflow, drawPathAndHandleLogic]);

  /**
   * Called when the session reaches a terminal state. Does a final full fetch.
   */
  const onSessionComplete = useCallback(async () => {
    // Same as onStatusChanged but guaranteed to run even if one is in flight
    fetchingRef.current = false;
    await onStatusChanged();
  }, [onStatusChanged]);

  const getLatestDraftVersion = async (projectId) => {
    await send_request(
      `project-service/project/last-version/${projectId}`,
      "",
      "",
      "get"
    ).then((res) => {
      if (!res || !res.data) return;
      let { draftVersion } = res.data;
      if (draftVersion) {
        setDraftVersion(draftVersion);
      }
    });
  };

  const getLatestProductionVersion = (projectId) => {
    loadPublishedProject(projectId).then((response) => {
      if (!response || !response.data) return;
      let productionVersion = response.data.version;
      if (productionVersion) {
        setProductionVersion(productionVersion);
      }
    });
  };
  const getWorkflowProgressData = async () => {
    threads = {};
    setSessionData({});

    setNodes([]);
    setEdges([]);

    setIsLatestRun(true);
    setShowTerminateButton(false);

    // Get the session data
    let url = `project-service/project/get_log/${id}/${project_session_id}`;

    // If it's a test, fetch the test logs
    if (isTestSession)
      url = `project-service/test/get_test_log/${id}/${project_session_id}`;

    return await send_request(url, "", {}, "")
      .then((res) => {
        setSessionData(res.data);
        return res.data;
      })
      .then(async (data) => {
        await getWorkflowData(data);
      })
      .then(async () => {
        await getReruns();
        setCanvasLoading(false); // Set canvas loading to false
      })
      .catch((e) => {
        window.location.href = `/project/logs/${id}`; // Go back on error
        console.log(e);
      });
  };

  const handleCloseRerunModal = () => {
    setSelectedSectionKey(null);
    setIsPanelOpen(false);
  };

  // Function to trigger refresh when component is retried
  const triggerRefresh = () => {
    setIsPolling(true);
  };

  return !canvasLoading && workflow && sessionData && reRuns ? (
    <RootWrapper isSplitView={props.isSplitView}>
      <CanvasContext.Provider
        value={{
          value: [[], () => {}],
          modeInfo: ["PROGRESSVIEW", () => {}],
          currentSelected: selectedComponentId
            ? { componentId: selectedComponentId }
            : {},
          workflow: useProgressViewStore.getState().canvas,
          handleAlterThreadPath: handleAlterThreadPath,
          isMainFlowSelected: isMainFlowSelected,
        }}
      >
        <ProgressViewPageHeader
          title={workflow.name}
          onBack={() => {
            const url = `/project/logs/${id}?test=${Boolean(
              isTestSession
            )}&viewSession=${project_session_id}`;
            navigate(url);
          }}
          workflow={workflow}
          sessionData={sessionData}
          projectId={id}
          sessionId={project_session_id}
          getSessionData={getWorkflowProgressData}
          onSessionTerminated={() => {
            setSelectedSectionKey(null);
            setIsPanelOpen(false);
          }}
          showTerminateButton={showTerminateButton}
          isTest={isTestSession}
          testId={sessionData && sessionData.projectTestId}
          isPolling={isPolling}
          setIsPolling={setIsPolling}
          isSplitView={props.isSplitView}
          navigate={navigate}
        />
        {hasManagedRunsAccess && (
          <ManagedRunOperatorPanel
            projectId={id}
            projectSessionId={project_session_id}
            isTest={isTestSession}
            workflowExtras={workflow?.extras}
            onOpenChat={(managedRun) => {
              if (!managedRun?.chatSessionId) return;
              const operatorAgentId = managedRun.agentId || "workflow-operator";
              navigate(
                `/agents/chats?agent=${encodeURIComponent(
                  operatorAgentId
                )}&sessionId=${encodeURIComponent(managedRun.chatSessionId)}`
              );
            }}
          />
        )}
        <CanvasDiv ref={reactFlowWrapper}>
          {isPolling && (
            <SessionPolling
              sessionId={project_session_id}
              isPolling={isPolling}
              setIsPolling={setIsPolling}
              onStatusChanged={onStatusChanged}
              onSessionComplete={onSessionComplete}
              isSplitView={props.isSplitView}
            />
          )}
          <TopGrid
            sessionData={sessionData}
            projectId={id}
            sessionId={project_session_id}
            isTest={isTestSession}
            setLoadReRun={setLoadReRun}
            isLatestRun={isLatestRun}
            setIsLatestRun={setIsLatestRun}
            reRuns={reRuns}
            setSessionData={(newSession, isLatestReRun) => {
              setSessionData(newSession);
              handleLogic(
                newSession,
                invertThreadPath(loopPath),
                isLatestReRun
              );
            }}
          />
          <SessionDocumentPanel
            isOpen={isPanelOpen}
            onClose={() => {
              setIsPanelOpen(false);
              setSelectedSectionKey(null);
            }}
            isExpanded={isPanelExpanded}
            setIsExpanded={setIsPanelExpanded}
            isSplitView={props.isSplitView}
            sessionData={sessionData}
            orderedSessionComponents={orderedSessionComponents}
            selectedSectionKey={selectedSectionKey}
            selectionNonce={selectionNonce}
            externalSearchQuery={externalSearchQuery}
            projectId={id}
            sessionId={project_session_id}
            isTest={isTestSession}
            projectTestId={sessionData && sessionData.projectTestId}
            isLatestRun={isLatestRun}
            reRuns={reRuns}
            setSessionData={(newSession, isLatestReRun) => {
              setSessionData(newSession);
              handleLogic(
                newSession,
                invertThreadPath(loopPath),
                isLatestReRun
              );
            }}
            setIsLatestRun={setIsLatestRun}
            setLoadReRun={setLoadReRun}
            triggerRefresh={triggerRefresh}
            refreshSessions={props.refreshSessions}
            getWorkflowProgressData={getWorkflowProgressData}
            handleAlterThreadPath={handleAlterThreadPath}
          />
          <Canvas
            mode={"PROGRESSVIEW"}
            isTemplate={false}
            nodes={nodes}
            setNodes={setNodes}
            edges={edges}
            setEdges={setEdges}
            reactFlowWrapper={reactFlowWrapper}
            reactFlowInstance={reactFlowInstance}
            setReactFlowInstance={setReactFlowInstance}
            handleCloseRerunModal={handleCloseRerunModal}
          />
        </CanvasDiv>
        {!isReady && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1100,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(0,0,0,0.6)"
                  : "rgba(255,255,255,0.85)",
              backdropFilter: "blur(2px)",
              pointerEvents: "all",
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading session…
            </Typography>
          </Box>
        )}
      </CanvasContext.Provider>
    </RootWrapper>
  ) : (
    <StyledSkeleton
      variant="rectangular"
      width="100%"
      height={props.isSplitView ? "100%" : "100vh"}
    />
  );
}

export default WorkflowProgressView;
